import type { AxiosError } from 'axios';

export const SUBSCRIPTION_REQUIRED_EVENT = 'wavvest:subscription-required';
export const SUBSCRIPTION_RECOVERY_PATH = '/subscriptions';

const RETURN_PATH_STORAGE_KEY = 'subscription_return_path';
const PUBLIC_PATHS = [
  /^\/$/,
  /^\/login$/,
  /^\/firm\/[^/]+(?:\/login|\/reset-password)?$/,
  /^\/sign-up$/,
  /^\/reset-password$/,
  /^\/complete-profile$/,
  /^\/meetinghub\/oauth$/,
  /^\/stripe-config$/,
];

type SubscriptionRequiredDetail = {
  kind: 'SUBSCRIPTION_REQUIRED';
  reason: string;
  recovery_action: 'manage_billing' | 'contact_firm_admin';
  can_manage_billing: boolean;
};

const normalizePathname = (pathname: string): string => pathname.replace(/\/+$/, '') || '/';

export const getSubscriptionRequiredDetail = (
  error: unknown,
): SubscriptionRequiredDetail | null => {
  const candidate = error as AxiosError<{ detail?: Partial<SubscriptionRequiredDetail> }>;
  if (candidate?.response?.status !== 403) {
    return null;
  }

  const detail = candidate.response.data?.detail;
  if (
    detail?.kind !== 'SUBSCRIPTION_REQUIRED' ||
    typeof detail.reason !== 'string' ||
    (detail.recovery_action !== 'manage_billing' &&
      detail.recovery_action !== 'contact_firm_admin') ||
    typeof detail.can_manage_billing !== 'boolean'
  ) {
    return null;
  }

  return detail as SubscriptionRequiredDetail;
};

export const isSafeReturnPath = (path: string): boolean => {
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }

  try {
    const url = new URL(path, window.location.origin);
    return (
      url.origin === window.location.origin &&
      normalizePathname(url.pathname) !== SUBSCRIPTION_RECOVERY_PATH
    );
  } catch {
    return false;
  }
};

export const shouldCheckEntitlement = (pathname: string, authenticated: boolean): boolean => {
  const normalizedPathname = normalizePathname(pathname);
  return (
    authenticated &&
    normalizedPathname !== SUBSCRIPTION_RECOVERY_PATH &&
    !PUBLIC_PATHS.some((pattern) => pattern.test(normalizedPathname))
  );
};

export const rememberSubscriptionReturnPath = (path: string): void => {
  if (isSafeReturnPath(path)) {
    sessionStorage.setItem(RETURN_PATH_STORAGE_KEY, path);
  }
};

export const consumeSubscriptionReturnPath = (): string => {
  const storedPath = sessionStorage.getItem(RETURN_PATH_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
  return storedPath && isSafeReturnPath(storedPath) ? storedPath : '/home';
};

export const notifySubscriptionRequired = (error: unknown): void => {
  const detail = getSubscriptionRequiredDetail(error);
  if (!detail) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<SubscriptionRequiredDetail>(SUBSCRIPTION_REQUIRED_EVENT, { detail }),
  );
};
